import { useState } from 'react';
import ArticleStatusBadge from './ArticleStatusBadge.jsx';
import RewriteTool from './RewriteTool.jsx';
import VerifyTool from './VerifyTool.jsx';

const CATEGORIES = ['Unmanned', 'Manned', 'Airspace', 'AI', 'Manufacturing'];

const CATEGORY_COLOR = {
  Unmanned: 'bg-hxd-orange text-white',
  Manned: 'bg-hxd-ok text-white',
  Airspace: 'bg-blue-600 text-white',
  AI: 'bg-purple-600 text-white',
  Manufacturing: 'bg-hxd-muted text-white',
};

function CategoryPill({ category }) {
  const cls = CATEGORY_COLOR[category] || 'bg-hxd-muted text-white';
  return (
    <span
      className={`${cls} font-display text-[9px] font-bold uppercase tracking-display-sm px-1.5 py-0.5 rounded-[2px]`}
    >
      {category || '—'}
    </span>
  );
}

// Per-article card. Phase 2 implements inline editing of headline / summary /
// keyTakeaway / category. Feedback flow + AI tools land in Phase 3+.
export default function InlineArticle({
  article,
  index,
  totalCount,
  isoDate,
  onChange,
  onCut,
  onMoveUp,
  onMoveDown,
  onRemove,
  onAcceptRewrite,
  onVerifyResult,
}) {
  if (!article) return null;
  const isPromoted = article.editorPromoted === true;
  const [tool, setTool] = useState(null); // 'rewrite' | 'verify' | null
  const [cutPrompt, setCutPrompt] = useState(false);
  const [cutReason, setCutReason] = useState('');
  const [blackoutInput, setBlackoutInput] = useState('');

  // Parse the comma-separated input into a clean keyword list. Trims, drops
  // empties, dedups (case-insensitive), and caps each keyword at 60 chars to
  // match the server-side sanity guard.
  function parseBlackoutKeywords(raw) {
    if (!raw) return [];
    const seen = new Set();
    const out = [];
    for (const part of raw.split(',')) {
      const trimmed = part.trim();
      if (!trimmed || trimmed.length > 60) continue;
      const norm = trimmed.toLowerCase();
      if (seen.has(norm)) continue;
      seen.add(norm);
      out.push(trimmed);
    }
    return out;
  }
  const blackoutKeywords = parseBlackoutKeywords(blackoutInput);

  return (
    <div
      id={`article-${article._id}`}
      className="hxd-card mb-4 scroll-mt-32"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-display text-[10px] font-bold text-hxd-orange">
          {String(index + 1).padStart(2, '0')}
        </span>
        <CategoryPill category={article.category} />
        <ArticleStatusBadge
          status={article.status}
          editorPromoted={isPromoted}
        />
        {article.source?.url && (
          <a
            href={article.source.url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-hxd-orange hover:text-hxd-orange-bright underline underline-offset-2 transition-colors truncate max-w-[300px]"
            title={article.source.url}
          >
            <span className="truncate">
              {article.source.displayName || article.source.domain || article.source.url}
            </span>
            <span aria-hidden="true">↗</span>
          </a>
        )}
      </div>

      <div className="mb-3">
        <label className="hxd-label">Headline</label>
        <input
          className="hxd-input font-semibold"
          value={article.headline || ''}
          onChange={(e) => onChange('headline', e.target.value)}
          placeholder="Article headline…"
        />
      </div>

      <div className="mb-3">
        <label className="hxd-label">Summary</label>
        <textarea
          className="hxd-input min-h-[110px]"
          rows={5}
          value={article.summary || ''}
          onChange={(e) => onChange('summary', e.target.value)}
          placeholder="100-word summary…"
        />
      </div>

      <div className="mb-3">
        <label className="hxd-label">Key Takeaway</label>
        <input
          className="hxd-input"
          value={article.keyTakeaway || ''}
          onChange={(e) => onChange('keyTakeaway', e.target.value)}
          placeholder="One-sentence investor takeaway…"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="hxd-label">Category</label>
          <select
            className="hxd-input"
            value={article.category || ''}
            onChange={(e) => onChange('category', e.target.value)}
          >
            <option value="" disabled>
              Choose…
            </option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {article.categoryConfidence != null && (
          <div>
            <label className="hxd-label">Pipeline Confidence</label>
            <div className="px-3 py-2.5 text-[13px] text-hxd-muted bg-hxd-surface border border-hxd-border-soft rounded-[3px]">
              {Math.round(article.categoryConfidence * 100)}%
              {article.editorialReasoning && (
                <span className="block mt-1 text-[11px] italic">
                  {article.editorialReasoning}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-hxd-border-soft">
        <button
          type="button"
          onClick={onMoveUp || undefined}
          disabled={!onMoveUp}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Move article up within category"
          title="Move up within category"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown || undefined}
          disabled={!onMoveDown}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Move article down within category"
          title="Move down within category"
        >
          ↓
        </button>
        {onAcceptRewrite && (
          <button
            type="button"
            onClick={() => setTool(tool === 'rewrite' ? null : 'rewrite')}
            className={
              tool === 'rewrite'
                ? 'hxd-btn hxd-btn-primary hxd-btn-sm'
                : 'hxd-btn hxd-btn-ghost hxd-btn-sm'
            }
          >
            Rewrite
          </button>
        )}
        {onVerifyResult && (
          <button
            type="button"
            onClick={() => setTool(tool === 'verify' ? null : 'verify')}
            className={
              tool === 'verify'
                ? 'hxd-btn hxd-btn-secondary hxd-btn-sm'
                : 'hxd-btn hxd-btn-ghost hxd-btn-sm'
            }
          >
            Verify
          </button>
        )}
        {article.source?.url && (
          <a
            href={article.source.url}
            target="_blank"
            rel="noreferrer"
            className="hxd-btn hxd-btn-ghost hxd-btn-sm inline-flex items-center gap-1"
            title={article.source.url}
          >
            Open source
            <span aria-hidden="true">↗</span>
          </a>
        )}
        <span className="text-[11px] text-hxd-muted ml-2">
          {isPromoted ? 'Editor add' : 'Pipeline slot'}
        </span>
        <button
          type="button"
          onClick={() => {
            if (isPromoted) {
              onRemove?.();
              return;
            }
            setCutReason('');
            setBlackoutInput('');
            setCutPrompt(true);
          }}
          className="ml-auto hxd-btn hxd-btn-ghost hxd-btn-sm text-hxd-err hover:text-hxd-err"
        >
          {isPromoted ? 'Remove' : 'Cut'}
        </button>
      </div>

      {cutPrompt && (
        <div className="mt-4 pt-4 border-t border-hxd-border-soft">
          <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-err mb-1">
            Cutting article — back to the bench
          </div>
          <p className="text-[12px] text-hxd-muted mb-4">
            Both fields are optional — fill in either, both, or neither and hit
            Cut to bench.
          </p>

          <div className="mb-4">
            <label className="hxd-label">Why are you cutting this?</label>
            <textarea
              autoFocus
              className="hxd-input min-h-[70px]"
              rows={3}
              value={cutReason}
              onChange={(e) => setCutReason(e.target.value)}
              placeholder="e.g. duplicate of yesterday's lead, source thin, not investor-relevant…"
            />
          </div>

          <div>
            <label className="hxd-label">Blacklist words</label>
            <textarea
              className="hxd-input min-h-[70px]"
              rows={3}
              value={blackoutInput}
              onChange={(e) => setBlackoutInput(e.target.value)}
              placeholder="e.g. Boeing, autonomous, eVTOL — comma-separated. Pipeline will filter these going forward."
            />
            {blackoutKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {blackoutKeywords.map((kw) => (
                  <span
                    key={kw.toLowerCase()}
                    className="inline-flex items-center bg-hxd-err/10 text-hxd-err border border-hxd-err/30 rounded-[2px] px-2 py-0.5 font-mono text-[11px]"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              onClick={() => {
                setCutPrompt(false);
                setCutReason('');
                setBlackoutInput('');
              }}
              className="hxd-btn hxd-btn-ghost hxd-btn-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                onCut?.({ cutReason: cutReason.trim(), blackoutKeywords });
                setCutPrompt(false);
                setCutReason('');
                setBlackoutInput('');
              }}
              className="hxd-btn hxd-btn-primary hxd-btn-sm ml-auto"
            >
              Cut to bench
            </button>
          </div>
        </div>
      )}

      {tool === 'rewrite' && (
        <RewriteTool
          articleId={article._id}
          isoDate={isoDate}
          original={article.summary || ''}
          onAccept={(payload) => {
            onAcceptRewrite?.(payload);
            setTool(null);
          }}
          onClose={() => setTool(null)}
        />
      )}

      {tool === 'verify' && (
        <VerifyTool
          articleId={article._id}
          isoDate={isoDate}
          original={article.summary || ''}
          onResult={(payload) => onVerifyResult?.(payload)}
          onClose={() => setTool(null)}
        />
      )}
    </div>
  );
}
